import type { ReactNode } from 'react';

/** Full-viewport loading screen used before the app shell is available (auth, organization checks). */
export function FullScreenLoader({ label = 'Laden...' }: { label?: string }) {
  return (
    <div className="min-h-screen bg-app-gradient flex items-center justify-center">
      <div className="text-white text-center" role="status">
        <div className="animate-spin w-8 h-8 border-4 border-white/30 border-t-white rounded-full mx-auto mb-4" />
        <p>{label}</p>
      </div>
    </div>
  );
}

/** Centered spinner for content areas inside the app shell. `card` wraps it in a card surface. */
export function PageSpinner({ card = false }: { card?: boolean }) {
  return (
    <div
      className={card ? 'card p-12 flex items-center justify-center' : 'min-h-[40vh] flex items-center justify-center'}
      role="status"
      aria-label="Laden"
    >
      <div className="spinner" />
    </div>
  );
}

interface EmptyStateProps {
  icon: ReactNode;
  title: ReactNode;
  children?: ReactNode;
}

export function EmptyState({ icon, title, children }: EmptyStateProps) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">{icon}</div>
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

/**
 * Pulsing placeholder cards shown while list data loads.
 * Each entry in `lines` holds Tailwind size classes for one bar (default radius `rounded`).
 */
export function SkeletonCards({
  count,
  className,
  lines,
}: {
  count: number;
  className: string;
  lines: readonly string[];
}) {
  return (
    <div className={className} aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="card p-6 animate-pulse space-y-4">
          {lines.map((line, lineIndex) => (
            <div
              key={lineIndex}
              className={`${line} ${line.includes('rounded') ? '' : 'rounded '}bg-[var(--color-surface-highlight)]`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
